import React, { useEffect, useState } from 'react';
import {
    Table,
    Button,
    Tag,
    Space,
    Input,
    Select,
    DatePicker,
    Card,
    Row,
    Col,
    Statistic,
    Modal,
    Form,
    InputNumber,
    message,
    Typography,
    Alert,
} from 'antd';
import {
    PlusOutlined,
    SearchOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { workLogAPI, projectAPI } from '../../services/api';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const WorkLogPage = () => {
    const [workLogs, setWorkLogs] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [form] = Form.useForm();
    const { user } = useSelector((state) => state.auth);

    const [filters, setFilters] = useState({
        project_id: '',
        status: '',
        start_date: '',
        end_date: '',
    });

    useEffect(() => {
        fetchWorkLogs();
        fetchProjects();
    }, []);

    const fetchWorkLogs = async () => {
        setLoading(true);
        try {
            const response = await workLogAPI.getAll(filters);
            setWorkLogs(response.data.work_logs);
        } catch (error) {
            message.error('작업 일지 조회 실패');
            console.error('Fetch work logs error:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchProjects = async () => {
        try {
            const response = await projectAPI.getAll();
            setProjects(response.data.projects);
        } catch (error) {
            console.error('Fetch projects error:', error);
        }
    };

    const handleCreate = async (values) => {
        try {
            const response = await workLogAPI.create({
                ...values,
                work_date: values.work_date.format('YYYY-MM-DD'),
            });

            // 공수 초과 경고 표시
            if (response.data.warning) {
                Modal.warning({
                    title: '공수 사용 경고',
                    content: response.data.warning,
                });
            }

            message.success(response.data.message || '작업 일지가 작성되었습니다');
            setModalVisible(false);
            form.resetFields();
            fetchWorkLogs();
        } catch (error) {
            message.error(error.response?.data?.message || '작업 일지 작성 실패');
        }
    };

    const handleSearch = () => {
        fetchWorkLogs();
    };

    const handleReset = () => {
        setFilters({ project_id: '', status: '', start_date: '', end_date: '' });
        setTimeout(() => fetchWorkLogs(), 0);
    };

    // 통계 계산
    const stats = {
        total: workLogs.length,
        total_hours: workLogs.reduce((sum, log) => sum + parseFloat(log.hours || 0), 0),
        approved: workLogs.filter((log) => log.status === 'approved').length,
        pending: workLogs.filter((log) => log.status === 'submitted').length,
    };

    const statusMap = {
        draft: { text: '임시저장', color: 'default' },
        submitted: { text: '제출됨', color: 'processing' },
        approved: { text: '승인됨', color: 'success' },
        rejected: { text: '반려됨', color: 'error' },
    };

    const workTypeMap = {
        development: '개발',
        design: '설계',
        meeting: '회의',
        research: '조사/연구',
        documentation: '문서작업',
        support: '지원',
        other: '기타',
    };

    const columns = [
        {
            title: '작업일',
            dataIndex: 'work_date',
            key: 'work_date',
            width: 120,
            render: (date) => dayjs(date).format('YYYY-MM-DD'),
        },
        {
            title: '프로젝트',
            dataIndex: ['project', 'name'],
            key: 'project',
            ellipsis: true,
        },
        {
            title: '작업자',
            dataIndex: ['user', 'name'],
            key: 'user',
            width: 100,
        },
        {
            title: '작업시간',
            dataIndex: 'hours',
            key: 'hours',
            width: 100,
            align: 'center',
            render: (hours) => `${hours}시간`,
        },
        {
            title: '작업유형',
            dataIndex: 'work_type',
            key: 'work_type',
            width: 100,
            render: (type) => workTypeMap[type] || type,
        },
        {
            title: '인건비',
            dataIndex: 'labor_cost',
            key: 'labor_cost',
            width: 120,
            align: 'right',
            render: (cost) => `${parseInt(cost || 0).toLocaleString()}원`,
        },
        {
            title: '상태',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => {
                const statusInfo = statusMap[status] || {};
                return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
            },
        },
        {
            title: '작업내용',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={2}>작업 일지</Title>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setSelectedLog(null);
                        form.resetFields();
                        setModalVisible(true);
                    }}
                    size="large"
                >
                    작업 일지 작성
                </Button>
            </div>

            {/* 통계 카드 */}
            <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="전체 작업일지"
                            value={stats.total}
                            suffix="건"
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="총 작업시간"
                            value={stats.total_hours}
                            precision={1}
                            suffix="시간"
                            prefix={<ClockCircleOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="승인됨"
                            value={stats.approved}
                            suffix="건"
                            prefix={<CheckCircleOutlined />}
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="승인 대기"
                            value={stats.pending}
                            suffix="건"
                            prefix={<WarningOutlined />}
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* 검색 필터 */}
            <Card style={{ marginTop: 24 }}>
                <Space wrap>
                    <Select
                        placeholder="프로젝트"
                        value={filters.project_id}
                        onChange={(value) => setFilters({ ...filters, project_id: value })}
                        options={[
                            { label: '전체', value: '' },
                            ...projects.map((p) => ({ label: p.name, value: p.id })),
                        ]}
                        style={{ width: 200 }}
                    />
                    <Select
                        placeholder="상태"
                        value={filters.status}
                        onChange={(value) => setFilters({ ...filters, status: value })}
                        options={[
                            { label: '전체', value: '' },
                            { label: '제출됨', value: 'submitted' },
                            { label: '승인됨', value: 'approved' },
                            { label: '반려됨', value: 'rejected' },
                        ]}
                        style={{ width: 120 }}
                    />
                    <RangePicker
                        onChange={(dates) => {
                            if (dates) {
                                setFilters({
                                    ...filters,
                                    start_date: dates[0].format('YYYY-MM-DD'),
                                    end_date: dates[1].format('YYYY-MM-DD'),
                                });
                            } else {
                                setFilters({ ...filters, start_date: '', end_date: '' });
                            }
                        }}
                    />
                    <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                        검색
                    </Button>
                    <Button onClick={handleReset}>초기화</Button>
                </Space>
            </Card>

            {/* 작업 일지 테이블 */}
            <Card style={{ marginTop: 24 }}>
                <Table
                    columns={columns}
                    dataSource={workLogs}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 1200 }}
                />
            </Card>

            {/* 작업 일지 작성 모달 */}
            <Modal
                title="작업 일지 작성"
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                }}
                footer={null}
                width={700}
            >
                <Alert
                    message="공수 통제 안내"
                    description="배정된 공수의 95% 사용 시 경고, 100% 초과 시 알림이 PM에게 전송됩니다."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />

                <Form form={form} layout="vertical" onFinish={handleCreate}>
                    <Form.Item
                        name="project_id"
                        label="프로젝트"
                        rules={[{ required: true, message: '프로젝트를 선택해주세요' }]}
                    >
                        <Select
                            placeholder="프로젝트 선택"
                            options={projects.map((p) => ({
                                label: `${p.name} (${p.code})`,
                                value: p.id,
                            }))}
                            showSearch
                            filterOption={(input, option) =>
                                option.label.toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="work_date"
                                label="작업일"
                                rules={[{ required: true, message: '작업일을 선택해주세요' }]}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="hours"
                                label="작업시간"
                                rules={[{ required: true, message: '작업시간을 입력해주세요' }]}
                            >
                                <InputNumber
                                    min={0.5}
                                    max={999}
                                    step={0.5}
                                    style={{ width: '100%' }}
                                    addonAfter="시간"
                                    placeholder="한 번에 여러 날짜 공수 입력 가능"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="work_type"
                        label="작업 유형"
                        rules={[{ required: true, message: '작업 유형을 선택해주세요' }]}
                    >
                        <Select
                            placeholder="작업 유형 선택"
                            options={[
                                { label: '개발', value: 'development' },
                                { label: '설계', value: 'design' },
                                { label: '회의', value: 'meeting' },
                                { label: '조사/연구', value: 'research' },
                                { label: '문서작업', value: 'documentation' },
                                { label: '지원', value: 'support' },
                                { label: '기타', value: 'other' },
                            ]}
                        />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="작업 내용"
                        rules={[{ required: true, message: '작업 내용을 입력해주세요' }]}
                    >
                        <TextArea rows={3} placeholder="수행한 작업 내용을 상세히 작성해주세요" />
                    </Form.Item>

                    <Form.Item name="achievement" label="작업 성과">
                        <TextArea rows={2} placeholder="완료한 결과물이나 성과를 작성해주세요" />
                    </Form.Item>

                    <Form.Item>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button
                                onClick={() => {
                                    setModalVisible(false);
                                    form.resetFields();
                                }}
                            >
                                취소
                            </Button>
                            <Button type="primary" htmlType="submit">
                                작성
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default WorkLogPage;